// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type AccountOpeningFormData,
  type AccountUsageFormData,
  type LegalDeclarationsFormData,
  type PersonalDataFormData,
  type WealthSourceFundsFormData,
  getInversisOptionId,
} from '../types';

type KycSingleAnswer = [answerId: number];

type KycOpenQuestionAnswer = [answerId: number, csv: string];

type KycMultipleAnswer = number[];

export type KycAnswerValue =
  | KycSingleAnswer
  | KycOpenQuestionAnswer
  | KycMultipleAnswer;

const sanitizeCsvPart = (value?: string): string => {
  return (value ?? '').trim().replace(/,/g, ' ');
};

const buildAdditionalDataCsv = (...parts: Array<string | undefined>): string => {
  return parts.map(sanitizeCsvPart).join(',');
};

const getQuestionId = (questionCode: string): string => {
  return getInversisOptionId(questionCode, 'kycQuestions');
};

const getAnswerId = (questionCode: string, answerSlug: string): number => {
  return Number(getInversisOptionId(`${questionCode}.${answerSlug}`, 'kycAnswers'));
};

const buildSingleAnswer = (
  questionCode: string,
  answerSlug: string,
): KycSingleAnswer => {
  return [getAnswerId(questionCode, answerSlug)];
};

const buildMultipleAnswer = (
  questionCode: string,
  answerSlugs: readonly string[],
): KycMultipleAnswer => {
  return answerSlugs
    .map(answerSlug => getAnswerId(questionCode, answerSlug))
    .sort((a, b) => a - b);
};

export const buildWealthSourceFundsKycAnswers = (
  wealthSourceFunds: WealthSourceFundsFormData,
): Record<string, KycAnswerValue> => {
  return {
    [getQuestionId('FUNDS_ORIGIN')]: buildMultipleAnswer(
      'FUNDS_ORIGIN',
      wealthSourceFunds.sourceOfFunds,
    ),
    [getQuestionId('ESTIMATED_WEALTH')]: buildSingleAnswer(
      'ESTIMATED_WEALTH',
      wealthSourceFunds.approximateWealth,
    ),
    [getQuestionId('MAIN_INCOME')]: buildSingleAnswer(
      'MAIN_INCOME',
      wealthSourceFunds.mainIncomeSource,
    ),
    [getQuestionId('ANNUAL_INCOME')]: buildSingleAnswer(
      'ANNUAL_INCOME',
      wealthSourceFunds.estimatedAnnualIncome,
    ),
  };
};

export const buildAccountUsageKycAnswers = (
  accountUsage: AccountUsageFormData,
): Record<string, KycAnswerValue> => {
  return {
    [getQuestionId('ACCOUNT_PURPOSE')]: buildMultipleAnswer(
      'ACCOUNT_PURPOSE',
      accountUsage.accountPurpose,
    ),
    [getQuestionId('MONTHLY_OPS')]: buildSingleAnswer(
      'MONTHLY_OPS',
      accountUsage.monthlyOperations,
    ),
    [getQuestionId('MONTHLY_TRANSFERS')]: buildSingleAnswer(
      'MONTHLY_TRANSFERS',
      accountUsage.monthlyTransfers,
    ),
    [getQuestionId('ACCOUNT_CHEQUES')]: buildSingleAnswer(
      'ACCOUNT_CHEQUES',
      accountUsage.accountCheques,
    ),
  };
};

export const buildLegalDeclarationsKycAnswers = (
  personalData: PersonalDataFormData,
  legalDeclarations: LegalDeclarationsFormData,
): Record<string, KycAnswerValue> => {
  const declarantFullName = [
    personalData.first_name,
    personalData.second_name,
    personalData.first_surname,
    personalData.second_surname,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const publicRoleAnswer: KycAnswerValue =
    legalDeclarations.isPoliticallyExposedPerson
      ? [
          getAnswerId('PUBLIC_ROLE', 'public-role-yes'),
          buildAdditionalDataCsv(
            declarantFullName,
            legalDeclarations.pepDetail?.position,
            legalDeclarations.pepDetail?.institution,
            legalDeclarations.pepDetail?.endDate,
          ),
        ]
      : buildSingleAnswer('PUBLIC_ROLE', 'public-role-no');

  const prpExtraIncomeAnswer: KycAnswerValue =
    legalDeclarations.isPoliticallyExposedPerson &&
    legalDeclarations.pepDetail?.hasRelatedAdditionalIncome
      ? [
          getAnswerId('PRP_EXTRA_INCOME', 'prp-extra-income-yes'),
          buildAdditionalDataCsv(
            legalDeclarations.pepDetail.additionalIncomeDetail,
          ),
        ]
      : buildSingleAnswer('PRP_EXTRA_INCOME', 'prp-extra-income-no');

  const pepRelatedAnswer: KycAnswerValue = legalDeclarations.isRelatedToPublicPerson
    ? [
        getAnswerId('PEP_RELATED', 'pep-related-yes'),
        buildAdditionalDataCsv(
          legalDeclarations.publicPersonRelationshipDetail?.relationship,
          legalDeclarations.publicPersonRelationshipDetail?.personName,
          legalDeclarations.publicPersonRelationshipDetail?.publicPosition,
          legalDeclarations.publicPersonRelationshipDetail?.institution,
          '',
        ),
      ]
    : buildSingleAnswer('PEP_RELATED', 'pep-related-no');

  return {
    [getQuestionId('PUBLIC_ROLE')]: publicRoleAnswer,
    [getQuestionId('PRP_EXTRA_INCOME')]: prpExtraIncomeAnswer,
    [getQuestionId('PEP_RELATED')]: pepRelatedAnswer,
  };
};

export const buildKycAnswersFromAccountOpening = (
  data: Omit<AccountOpeningFormData, 'signature'>,
): Record<string, KycAnswerValue> => {
  return {
    ...buildWealthSourceFundsKycAnswers(data.wealthSourceFunds),
    ...buildAccountUsageKycAnswers(data.accountUsage),
    ...buildLegalDeclarationsKycAnswers(data.personalData, data.legalDeclarations),
  };
};
